import { useState } from 'react'
import type { HazardDetailModel, HazardDetailSection } from '@buxus/shared'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'
import { BuxusAccordion } from './components/BuxusAccordion'
import { InlineSpinner } from './components/InlineSpinner'
import { OperatorInlineImage } from './OperatorInlineImage'

type HazardDetailViewProps = {
  model: HazardDetailModel
  operatorId: string | null
  onViewSds?: () => void | Promise<void>
  sdsOpening?: boolean
  analytics?: ScreenInteractionContext
}

function fieldsTable(fields: NonNullable<HazardDetailSection['fields']>) {
  const rows = fields.filter((r) => r.value.trim())
  if (!rows.length) return null
  return (
    <table className="buxus-data-card-table">
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <th scope="row">{r.label}</th>
            <td className="buxus-data-card-multiline">{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Mobile `HazardStorageLocationCard` — grey label bar, value stacked underneath. */
function stackedFieldsCard(fields: NonNullable<HazardDetailSection['stackedFields']>) {
  const rows = fields.filter((r) => r.value.trim())
  if (!rows.length) return null
  return (
    <div className="hazard-detail-stacked-card">
      {rows.map((r) => (
        <div key={r.label} className="hazard-detail-stacked-item">
          <p className="hazard-detail-stacked-label">{r.label}</p>
          <p className="hazard-detail-stacked-value">{r.value}</p>
        </div>
      ))}
    </div>
  )
}

function mixtureTable(rows: NonNullable<HazardDetailSection['mixture']>) {
  if (!rows.length) return null
  return (
    <table className="hazard-detail-mixture-table">
      <thead>
        <tr>
          <th scope="col">Product</th>
          <th scope="col">%</th>
          <th scope="col">CAS #</th>
          <th scope="col">EHS</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.productName}-${i}`}>
            <td>{r.productName}</td>
            <td>{r.percentage}</td>
            <td>{r.casNumber}</td>
            <td>{r.ehs}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function leakImageGroup(
  images: NonNullable<HazardDetailSection['leakImages']>,
  operatorId: string | null,
) {
  const groups: { title: string; key: 'sight' | 'smell' | 'sound' }[] = [
    { title: 'Sight', key: 'sight' },
    { title: 'Smell', key: 'smell' },
    { title: 'Sound', key: 'sound' },
  ]
  return (
    <div className="hazard-detail-leak-images">
      {groups.map((g) => {
        const items = images.filter((img) => img.group === g.key)
        if (!items.length) return null
        return (
          <div key={g.key} className="hazard-detail-leak-group">
            <h4 className="hazard-detail-leak-group-title">{g.title}</h4>
            {items.map((img) => (
              <div key={img.id} className="hazard-detail-leak-card">
                {operatorId ? (
                  <OperatorInlineImage
                    operatorId={operatorId}
                    fileId={img.id}
                    fileExt={img.fileExt}
                    alt={img.title || 'Leak recognition'}
                    className="hazard-detail-leak-image"
                    zoomable
                  />
                ) : (
                  <p className="operator-inline-image-fallback">
                    Select an operator to view leak recognition images.
                  </p>
                )}
                {img.title ? <p className="hazard-detail-leak-title">{img.title}</p> : null}
                {img.description ? (
                  <p className="hazard-detail-leak-desc">{img.description}</p>
                ) : null}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

function HazardHtmlBlocks({ blocks }: { blocks: NonNullable<HazardDetailSection['htmlBlocks']> }) {
  return (
    <div className="hazard-detail-html-blocks">
      {blocks.map((block) => (
        <div key={block.heading} className="hazard-detail-html-block">
          <p className="buxus-html-subheader">{block.heading}</p>
          <div
            className="rich hazard-detail-html-block-body"
            dangerouslySetInnerHTML={{ __html: block.html }}
          />
        </div>
      ))}
    </div>
  )
}

function HazardSectionBody({
  section,
  operatorId,
}: {
  section: HazardDetailSection
  operatorId: string | null
}) {
  return (
    <>
      {section.htmlBlocks?.length ? <HazardHtmlBlocks blocks={section.htmlBlocks} /> : null}
      {section.html ? (
        <div
          className="rich hazard-detail-section-body"
          dangerouslySetInnerHTML={{ __html: section.html }}
        />
      ) : null}
      {section.stackedFields?.length ? stackedFieldsCard(section.stackedFields) : null}
      {section.fields ? fieldsTable(section.fields) : null}
      {section.mixture ? mixtureTable(section.mixture) : null}
      {section.bullets?.map((b) =>
        b.items.length ? (
          <div key={b.heading} className="hazard-detail-bullets">
            <p className="buxus-html-subheader">{b.heading}</p>
            <ul>
              {b.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null,
      )}
      {section.leakImages?.length ? leakImageGroup(section.leakImages, operatorId) : null}
    </>
  )
}

export function HazardDetailView({
  model,
  operatorId,
  onViewSds,
  sdsOpening = false,
  analytics,
}: HazardDetailViewProps) {
  const hasProduct = !!model.productSubType
  const hasStorage = model.showStorageNameHeader && !!model.storageName
  const defaultExpandedIds =
    model.sections.length === 1 ? [model.sections[0]!.id] : undefined
  const [expandedIds, setExpandedIds] = useState<string[]>(() => defaultExpandedIds ?? [])

  return (
    <article className="hazard-detail">
      {(hasProduct || hasStorage) && (
        <header className="hazard-detail-intro">
          {hasProduct ? (
            <div className="hazard-detail-intro-block">
              <p className="hazard-detail-intro-label">Product</p>
              <p className="hazard-detail-intro-value">{model.productSubType}</p>
            </div>
          ) : null}
          {hasStorage ? (
            <div className="hazard-detail-intro-block">
              <p className="hazard-detail-intro-label">Storage Location Name</p>
              <p className="hazard-detail-intro-value">{model.storageName}</p>
            </div>
          ) : null}
        </header>
      )}
      {model.sdsTitle && model.sdsFileId && onViewSds ? (
        <button
          type="button"
          className="hazard-detail-sds-btn"
          disabled={sdsOpening}
          aria-busy={sdsOpening}
          onClick={() => void onViewSds()}
        >
          View SDS
          {sdsOpening ? <InlineSpinner label="Opening file" /> : null}
        </button>
      ) : null}
      <BuxusAccordion
        className="hazard-detail-accordion"
        expandedIds={expandedIds}
        onExpandedIdsChange={setExpandedIds}
        analytics={analytics}
        items={model.sections.map((section) => ({
          id: section.id,
          title: section.title,
          content: <HazardSectionBody section={section} operatorId={operatorId} />,
        }))}
      />
    </article>
  )
}
